import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import authService, {
  type AuthUserResponse,
  type AuthLoginResponse,
  type AuthMeResponse,
} from "../services/authService";
import { fetchCompanyProfile, logout as logoutCompany } from "./companySlice";

export interface AuthUser {
  id: string;
  email: string;
  username?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  dateOfBirth?: string | null;
  imageMediaId?: string | null;
  imageUrl?: string | null;
  status?: string;
  companyId?: string | null;
  role?: {
    id: string;
    name: string;
    roleType?: string;
  } | null;
}

interface AuthState {
  user: AuthUser | null;
  role: string | null;
  permissions: string[];
  isAuthenticated: boolean;
  initialized: boolean;
  /** 'company' or 'user' — set after login */
  principalType: "company" | "user" | null;
  loading: boolean;
  error: string | null;
  errorCode: string | null;
  errorEmail: string | null;
}

const initialState: AuthState = {
  user: null,
  role: null,
  permissions: [],
  isAuthenticated: false,
  initialized: false,
  principalType: null,
  loading: false,
  error: null,
  errorCode: null,
  errorEmail: null,
};

function mapApiUser(apiUser: AuthUserResponse): AuthUser {
  return {
    id: apiUser.id,
    email: apiUser.email,
    username: apiUser.username ?? undefined,
    firstName: apiUser.firstName ?? undefined,
    lastName: apiUser.lastName ?? undefined,
    dateOfBirth: apiUser.dateOfBirth ?? undefined,
    imageMediaId: apiUser.imageMediaId ?? undefined,
    imageUrl: apiUser.imageUrl ?? undefined,
    role: apiUser.role
      ? {
          id: apiUser.roleId ?? "",
          name: apiUser.role,
          roleType: apiUser.roleType ?? undefined,
        }
      : null,
  };
}

function getErrorMessage(err: unknown, fallback: string): string {
  return getLoginFailure(err, fallback).message;
}

function getLoginFailure(
  err: unknown,
  fallback: string,
): { message: string; code?: string; email?: string } {
  try {
    const data = (
      err as {
        response?: {
          data?: {
            message?: string | string[];
            error?: {
              code?: string;
              message?: string | string[];
              details?: { email?: string; next?: string };
            };
          };
        };
      }
    )?.response?.data;
    const raw = data?.error?.message ?? data?.message;
    const message = Array.isArray(raw)
      ? raw.join(". ") || fallback
      : typeof raw === "string"
        ? raw
        : fallback;
    return {
      message,
      code: data?.error?.code,
      email: data?.error?.details?.email,
    };
  } catch {
    return { message: fallback };
  }
}

function persistUserRoleType(roleType?: string | null) {
  if (typeof localStorage === "undefined") return;
  if (roleType) localStorage.setItem("roleType", roleType);
  else localStorage.removeItem("roleType");
}

export const loginUser = createAsyncThunk<
  { user: AuthUser; permissions: string[] },
  { email: string; password: string },
  { rejectValue: { message: string; code?: string; email?: string } }
>("auth/loginUser", async (credentials, { rejectWithValue }) => {
  try {
    const res = await authService.login(credentials);
    const data =
      (res.data as { data?: AuthLoginResponse })?.data ??
      (res.data as unknown as AuthLoginResponse);
    if (!data?.tokens) return rejectWithValue({ message: "Invalid response" });
    localStorage.setItem("accessToken", data.tokens.accessToken);
    localStorage.setItem("refreshToken", data.tokens.refreshToken);
    localStorage.setItem("principalType", "user");
    const user = mapApiUser(data.user);
    persistUserRoleType(user.role?.roleType);
    return {
      user,
      permissions: data.permissions ?? [],
    };
  } catch (err: unknown) {
    return rejectWithValue(getLoginFailure(err, "Login failed"));
  }
});

export const fetchUserProfile = createAsyncThunk<
  { user: AuthUser; permissions: string[] },
  { silent?: boolean } | undefined,
  { rejectValue: string }
>("auth/fetchUserProfile", async (options, { rejectWithValue }) => {
  try {
    const res = await authService.getMe({
      skipAuthRedirect: options?.silent === true,
    });
    const data =
      (res.data as { data?: AuthMeResponse })?.data ??
      (res.data as unknown as AuthMeResponse);
      if (!data?.user) return rejectWithValue("Invalid response");
      const user = mapApiUser(data.user);
      persistUserRoleType(user.role?.roleType);
      return {
        user,
        permissions: data.permissions ?? [],
      };
  } catch (err: unknown) {
    return rejectWithValue(getErrorMessage(err, "Failed to load profile"));
  }
});

export const initializeAuth = createAsyncThunk<
  "company" | "user" | null,
  void,
  { rejectValue: string }
>("auth/initialize", async (_, { dispatch, rejectWithValue }) => {
  const accessToken = localStorage.getItem("accessToken");
  const refreshToken = localStorage.getItem("refreshToken");
  const principalType = localStorage.getItem("principalType");

  if (
    !accessToken ||
    !refreshToken ||
    (principalType !== "company" && principalType !== "user")
  ) {
    return null;
  }

  try {
    if (principalType === "company") {
      await dispatch(fetchCompanyProfile({ silent: true })).unwrap();
      return "company";
    }

    await Promise.all([
      dispatch(fetchCompanyProfile({ silent: true })).unwrap(),
      dispatch(fetchUserProfile({ silent: true })).unwrap(),
    ]);
    return "user";
  } catch {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("principalType");
    localStorage.removeItem("roleType");
    dispatch(logoutCompany());
    return rejectWithValue("Session could not be restored");
  }
});

export const refreshPermissions = createAsyncThunk<
  string[],
  void,
  { state: { auth: AuthState }; rejectValue: string }
>("auth/refreshPermissions", async (_, { getState, rejectWithValue }) => {
  const accessToken = localStorage.getItem("accessToken");
  if (!accessToken) return rejectWithValue("No token");

  // Company sessions do not have user permissions. Treat them as an empty
  // permission set so App can refresh every authenticated session uniformly.
  if (!getState().auth.user) return [];

  try {
    const res = await authService.getMe({ skipAuthRedirect: true });
    const data =
      (res.data as { data?: AuthMeResponse })?.data ??
      (res.data as unknown as AuthMeResponse);
    if (!data?.user) return rejectWithValue("Invalid response");
    const permissions = data.permissions ?? [];
    localStorage.setItem("permissions", JSON.stringify(permissions));
    return permissions;
  } catch (err: unknown) {
    return rejectWithValue(
      getErrorMessage(err, "Failed to refresh permissions"),
    );
  }
});

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setAuthUser(
      state,
      action: PayloadAction<{ user: AuthUser; permissions: string[] }>,
    ) {
      state.user = action.payload.user;
      state.permissions = action.payload.permissions;
      state.role = action.payload.user.role?.name ?? null;
      state.isAuthenticated = true;
      state.initialized = true;
      state.principalType = "user";
      persistUserRoleType(action.payload.user.role?.roleType);
    },
    setCompanyPrincipal(state) {
      state.user = null;
      state.role = null;
      state.isAuthenticated = true;
      state.initialized = true;
      state.principalType = "company";
      state.permissions = [];
      state.loading = false;
      state.error = null;
      persistUserRoleType(null);
    },
    clearAuth(state) {
      localStorage.removeItem("principalType");
      persistUserRoleType(null);
      Object.assign(state, { ...initialState, initialized: true });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.errorCode = null;
        state.errorEmail = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.user = action.payload.user;
        state.permissions = action.payload.permissions;
        state.role = action.payload.user.role?.name ?? null;
        state.isAuthenticated = true;
        state.initialized = true;
        state.principalType = "user";
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message ?? "Login failed";
        state.errorCode = action.payload?.code ?? null;
        state.errorEmail = action.payload?.email ?? null;
      })
      .addCase(fetchUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.permissions = action.payload.permissions;
        state.role = action.payload.user.role?.name ?? null;
        state.isAuthenticated = true;
        state.principalType = "user";
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Failed to load profile";
      })
      .addCase(initializeAuth.pending, (state) => {
        state.initialized = false;
        state.loading = true;
        state.error = null;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.initialized = true;
        state.loading = false;
        if (action.payload === "company") {
          state.user = null;
          state.role = null;
          state.isAuthenticated = true;
          state.principalType = "company";
          state.permissions = [];
        } else if (action.payload === null) {
          state.user = null;
          state.role = null;
          state.permissions = [];
          state.isAuthenticated = false;
          state.principalType = null;
        }
      })
      .addCase(initializeAuth.rejected, (state, action) => {
        state.initialized = true;
        state.loading = false;
        state.user = null;
        state.role = null;
        state.permissions = [];
        state.isAuthenticated = false;
        state.principalType = null;
        state.error = action.payload ?? "Session could not be restored";
      })
      .addCase(refreshPermissions.fulfilled, (state, action) => {
        state.permissions = action.payload;
      })
      .addMatcher(
        (action) => action.type === "company/login/fulfilled",
        (state) => {
          state.user = null;
          state.role = null;
          state.permissions = [];
          state.isAuthenticated = true;
          state.initialized = true;
          state.principalType = "company";
          state.loading = false;
          state.error = null;
        },
      );
  },
});

export const { setAuthUser, setCompanyPrincipal, clearAuth } =
  authSlice.actions;
export default authSlice.reducer;
